import {
  createIdentitySession,
  publishIdentity,
  createRetentionProvider,
  toAyDimension,
} from '@redbrickmedia/duplex.user-identity'
import { OpenFeature } from '@openfeature/web-sdk'
import { useAnalytics } from '../composables/analytics/useAnalytics'
import debug from '../utils/console'

const logger = debug.withTag('plugins:user-identity')

/** AY custom dimension naming the provider that resolved the HEM. */
const HEM_PROVIDER_DIMENSION = 'custom_13'

export default defineNuxtPlugin({
  name: 'user-identity',
  // Deliberately ahead of 'assertive': resolution has to start before the AY
  // script loads so the HEM lookup overlaps it instead of queueing behind it.
  dependsOn: ['openfeature', 'user-segment'],
  setup: async () => {
    const { client } = await useOpenFeature()
    if (!client.getBooleanValue('enableUserIdentity', false)) {
      return
    }

    const { targetingKey } = OpenFeature.getContext()
    if (typeof targetingKey !== 'string' || !targetingKey) {
      logger.warn('No targetingKey available; skipping identity capture')
      return
    }

    const { track } = useAnalytics()

    // Assertive Yield depends on this plugin, so nothing here may throw —
    // an identity failure must never stop ads from loading.
    let session
    try {
      // Resolution starts here, before the AY script loads, so the Retention
      // round-trip overlaps AY rather than following it. A HEM cached from an
      // earlier pageview is read synchronously, so returning visitors already
      // carry it into the first publish below.
      //
      // No `source`: the EID source has to be a real domain buyers have a
      // mapping for, so it is left to the package to take from the page. Which
      // tenant it was travels in the AY dimensions instead.
      session = createIdentitySession({
        userId: targetingKey,
        provider: createRetentionProvider({
          onEvent: (event) =>
            event.type === 'script:load'
              ? track('NtpRetention_ScriptLoaded', { success: event.success })
              : track('NtpRetention_MatchResult', {
                  hem_resolved: event.hemResolved,
                  status: event.status,
                }),
        }),
      })
    } catch (error) {
      logger.warn('Failed to start identity session', error)
      return
    }

    // Account key and user-id submodules are the same across every property, so
    // both come from the package rather than app config. Publishing is
    // idempotent — modules are keyed by name — so the late-HEM pass replaces our
    // pubProvidedId entry rather than duplicating the list.
    const publish = () => {
      try {
        publishIdentity(session, { sendToGAM: false })
      } catch {
        logger.warn('Failed to publish identity to Prebid')
      }
    }

    // Written into the live analytics object rather than passed at init: the
    // HEM usually resolves after scriptLoadAY has already built `custom`, and
    // the value has to be replaced in place when it does. Reads 'UnResolved'
    // until then.
    const syncAyHemProvider = () => {
      const patch = toAyDimension(session.current, {
        dimension: HEM_PROVIDER_DIMENSION,
      })
      window.assertiveQueue = window.assertiveQueue || []
      window.assertiveQueue.push(() => {
        if (window.assertive?.analytics?.custom) {
          Object.assign(window.assertive.analytics.custom, patch)
        }
      })
    }

    // Publishing has to happen after AY: it applies its Prebid config once on
    // script load, so anything written before that is overwritten. AY drains
    // this queue once loaded, which is also why pushing to it here — before AY
    // exists — is safe.
    window.assertiveQueue = window.assertiveQueue || []
    window.assertiveQueue.push(publish)
    syncAyHemProvider()

    // A HEM landing before AY loads would publish into config AY then discards,
    // but the queued pass above re-publishes it afterwards, so either order
    // converges.
    session.onHem(() => {
      publish()
      syncAyHemProvider()
    })
  },
})
